import {
  Layout,
  Typography,
  DatePicker,
  Space,
  Table,
  Tag,
  Card,
  Row,
  Col,
  Empty,
  Spin,
  Drawer,
  Button,
  Statistic,
  Select,
} from "antd";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useState, useMemo, useCallback } from "react";
import dayjs, { Dayjs } from "dayjs";
import { TrophyOutlined, TeamOutlined, CheckCircleOutlined, ClockCircleOutlined, EyeOutlined } from "@ant-design/icons";
import { useGetTournamentReports, useTournamentBracket } from "@hooks";
import { TournamentDto, TournamentStatus } from "@types";
import TournamentBracketView from "@/components/tournament/TournamentBracketView";
import type { ColumnsType } from "antd/es/table";
import type { TournamentReportFilter } from "@hooks";

const { RangePicker } = DatePicker;

export const TournamentReportsPage = () => {
  const { t } = useTranslation();

  // Default to last 30 days
  const defaultFilter = useMemo<TournamentReportFilter>(
    () => ({
      startDate: dayjs().subtract(30, "day").startOf("day").toISOString(),
      endDate: dayjs().endOf("day").toISOString(),
    }),
    []
  );

  const [filter, setFilter] = useState<TournamentReportFilter>(defaultFilter);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: tournaments, isLoading } = useGetTournamentReports({
    ...filter,
    status: statusFilter,
  });

  const { data: bracketData, isLoading: bracketLoading } = useTournamentBracket(
    drawerOpen ? selectedTournamentId : null
  );

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setFilter({
        startDate: dates[0].startOf("day").toISOString(),
        endDate: dates[1].endOf("day").toISOString(),
      });
    }
  };

  const handleTournamentClick = useCallback((tournament: TournamentDto) => {
    setSelectedTournamentId(tournament.id);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedTournamentId(null);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case TournamentStatus.Completed:
        return "green";
      case TournamentStatus.InProgress:
      case TournamentStatus.SemiFinals:
      case TournamentStatus.Finals:
        return "blue";
      case TournamentStatus.Scheduled:
        return "orange";
      case TournamentStatus.Created:
        return "default";
      default:
        return "default";
    }
  };

  const columns: ColumnsType<TournamentDto> = [
    {
      title: t("tournamentReports.columns.title"),
      dataIndex: "title",
      key: "title",
      render: (text: string) => (
        <Space>
          <TrophyOutlined style={{ color: "#faad14" }} />
          <Typography.Text strong>{text}</Typography.Text>
        </Space>
      ),
    },
    {
      title: t("tournamentReports.columns.status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {t(`tournamentReports.status.${status.toLowerCase()}`) || status}
        </Tag>
      ),
    },
    {
      title: t("tournamentReports.columns.participants"),
      dataIndex: "participantCount",
      key: "participantCount",
      render: (count: number, record: TournamentDto) => (
        <Space>
          <TeamOutlined />
          <span>{count} / {record.bracketSize}</span>
        </Space>
      ),
    },
    {
      title: t("tournamentReports.columns.dateCreated"),
      dataIndex: "dateCreated",
      key: "dateCreated",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
    },
    {
      title: t("tournamentReports.columns.winner"),
      dataIndex: "winner",
      key: "winner",
      render: (winner: TournamentDto["winner"]) =>
        winner ? (
          <Space>
            <span>🥇</span>
            <Typography.Text>{winner.displayName}</Typography.Text>
          </Space>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        ),
    },
    {
      title: t("actions"),
      key: "actions",
      width: 120,
      render: (_: unknown, record: TournamentDto) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleTournamentClick(record)}
        >
          {t("tournamentReports.viewBracket")}
        </Button>
      ),
    },
  ];

  // Statistics
  const stats = useMemo(() => {
    if (!tournaments || tournaments.length === 0) {
      return { total: 0, completed: 0, inProgress: 0, totalParticipants: 0 };
    }
    return {
      total: tournaments.length,
      completed: tournaments.filter((t) => t.status === TournamentStatus.Completed).length,
      inProgress: tournaments.filter(
        (t) =>
          t.status === TournamentStatus.InProgress ||
          t.status === TournamentStatus.SemiFinals ||
          t.status === TournamentStatus.Finals
      ).length,
      totalParticipants: tournaments.reduce((sum, t) => sum + t.participantCount, 0),
    };
  }, [tournaments]);

  const hasData = tournaments && tournaments.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Layout>
        {/* Page Title */}
        <div style={{ display: "flex", marginBottom: 24 }}>
          <div
            style={{
              width: 3,
              backgroundColor: "#faad14",
              borderRadius: 100,
              marginRight: 12,
            }}
          />
          <Typography.Title level={2} style={{ margin: 0 }}>
            {t("tournamentReports.title")}
          </Typography.Title>
        </div>

        {/* Filters */}
        <Card style={{ marginBottom: 24 }}>
          <Space wrap size="middle">
            <Space>
              <Typography.Text strong>{t("tournamentReports.filters.dateRange")}:</Typography.Text>
              <RangePicker
                value={[
                  filter.startDate ? dayjs(filter.startDate) : null,
                  filter.endDate ? dayjs(filter.endDate) : null,
                ]}
                onChange={handleDateRangeChange}
                format="DD.MM.YYYY"
                allowClear={false}
              />
            </Space>
            <Space>
              <Typography.Text strong>{t("status")}:</Typography.Text>
              <Select
                style={{ width: 150 }}
                placeholder={t("tournamentReports.filters.allStatuses")}
                allowClear
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: TournamentStatus.Created, label: t("tournamentReports.status.created") },
                  { value: TournamentStatus.Scheduled, label: t("tournamentReports.status.scheduled") },
                  { value: TournamentStatus.InProgress, label: t("tournamentReports.status.inprogress") },
                  { value: TournamentStatus.SemiFinals, label: t("tournamentReports.status.semifinals") },
                  { value: TournamentStatus.Finals, label: t("tournamentReports.status.finals") },
                  { value: TournamentStatus.Completed, label: t("tournamentReports.status.completed") },
                ]}
              />
            </Space>
          </Space>
        </Card>

        {/* Statistics Cards */}
        {hasData && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title={t("tournamentReports.stats.total")}
                  value={stats.total}
                  prefix={<TrophyOutlined style={{ color: "#faad14" }} />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title={t("tournamentReports.stats.completed")}
                  value={stats.completed}
                  valueStyle={{ color: "#52c41a" }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title={t("tournamentReports.stats.inProgress")}
                  value={stats.inProgress}
                  valueStyle={{ color: "#1890ff" }}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title={t("tournamentReports.stats.participants")}
                  value={stats.totalParticipants}
                  prefix={<TeamOutlined />}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Tournament List */}
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin size="large" />
            <Typography.Text type="secondary" style={{ display: "block", marginTop: 16 }}>
              {t("tournamentReports.loading")}
            </Typography.Text>
          </div>
        ) : hasData ? (
          <Card
            style={{
              boxShadow: "rgba(0, 0, 0, 0.16) 1px 0px 5px",
              borderRadius: "8px",
            }}
          >
            <Table
              rowKey={(record) => record.id}
              size="middle"
              dataSource={tournaments}
              columns={columns}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => t("tournamentReports.totalItems", { total }),
              }}
              onRow={(record) => ({
                onClick: () => handleTournamentClick(record),
                style: { cursor: "pointer" },
              })}
            />
          </Card>
        ) : (
          <Empty
            description={t("tournamentReports.noData")}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ marginTop: 48 }}
          />
        )}

        {/* Bracket Drawer */}
        <Drawer
          title={
            <Space>
              <TrophyOutlined style={{ color: "#faad14" }} />
              {bracketData?.title || t("tournamentReports.bracketView")}
            </Space>
          }
          placement="right"
          width="80%"
          onClose={handleDrawerClose}
          open={drawerOpen}
          styles={{ body: { padding: 0 }, content: {
            borderRadius: 16,
          } }}
        >
          {bracketLoading ? (
            <div style={{ textAlign: "center", padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : bracketData ? (
            <TournamentBracketView bracket={bracketData} isLoading={bracketLoading} />
          ) : (
            <Empty description={t("tournamentReports.noBracketData")} />
          )}
        </Drawer>
      </Layout>
    </motion.div>
  );
};
